/**
 * Illustration animée de la connexion par QR code, à partir de
 * Secura-mobile/docs/animation-phone-pc.png.
 * Le dessin (téléphone, flèche, ordinateur) reste en place. Document, photo et
 * vidéo en sortent et suivent la flèche jusqu'au dossier.
 * - « scanned »  : le trajet se répète pendant la confirmation sur le téléphone ;
 * - « approved » : le trajet accélère et une coche apparaît sur l'écran.
 * ArrivalOverlay prend ensuite tout l'écran (voile bleu qui s'ouvre en cercle, « Bienvenue »).
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'framer-motion';
import still from '../../assets/link-transfer/still.png';
import cardDoc from '../../assets/link-transfer/doc.png';
import cardPhoto from '../../assets/link-transfer/photo.png';
import cardVideo from '../../assets/link-transfer/video.png';

const BLUE = '#0026E2';
const spring = { type: 'spring', stiffness: 260, damping: 26 };
const easeOut = [0.22, 1, 0.36, 1];

// Trajet, en % du cadre : écran du téléphone, au-dessus de la flèche, dossier.
const LEFT = ['13%', '20%', '38%', '56%', '71%'];
const TOP = ['50%', '40%', '24%', '30%', '43%'];
const SCALE = [0.42, 0.82, 1, 0.78, 0.32];
const OPACITY = [0, 1, 1, 1, 0];
const TIMES = [0, 0.16, 0.42, 0.72, 1];

const CARDS = [
  { src: cardDoc, width: '15.5%' },
  { src: cardPhoto, width: '14.5%' },
  { src: cardVideo, width: '13%' },
];

function FlyingCard({ src, width, index, fast, reduce }) {
  if (reduce) return null;
  const duration = fast ? 1.15 : 2.35;
  const delay = index * (fast ? 0.28 : 0.55);
  return (
    <motion.img
      src={src}
      alt=""
      draggable={false}
      className="absolute"
      style={{ width, x: '-50%', y: '-50%' }}
      initial={{ left: LEFT[0], top: TOP[0], opacity: 0, scale: SCALE[0] }}
      animate={{ left: LEFT, top: TOP, opacity: OPACITY, scale: SCALE }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: 'easeInOut',
        times: TIMES,
      }}
    />
  );
}

export function LinkTransfer({ stage }) {
  const reduce = useReducedMotion();
  const done = stage === 'approved';
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={spring}
      className="absolute inset-x-2 top-1/2 -translate-y-1/2 pointer-events-none"
      role="img"
      aria-label={done ? 'Connexion établie' : 'Transfert des fichiers vers l’ordinateur'}
    >
      <div className="relative w-full" style={{ aspectRatio: '1600 / 820' }}>
        <img src={still} alt="" draggable={false} className="absolute inset-0 h-full w-full select-none" />
        {CARDS.map((card, index) => (
          <FlyingCard key={`${stage}-${index}`} {...card} index={index} fast={done} reduce={reduce} />
        ))}
        {done ? (
          <motion.div
            className="absolute flex h-7 w-7 items-center justify-center rounded-full text-white"
            style={{ left: '78%', top: '13%', background: BLUE, x: '-50%', y: '-50%' }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ ...spring, delay: 0.35 }}
          >
            <svg viewBox="0 0 16 16" className="h-4 w-4" aria-hidden="true">
              <motion.path
                d="M3.2 8.2 l3 3.1 l6.4 -7"
                fill="none"
                stroke="#fff"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.35, delay: 0.5, ease: easeOut }}
              />
            </svg>
          </motion.div>
        ) : null}
      </div>
    </motion.div>
  );
}

const firstName = (user) => String(user?.nom_complet || user?.name || user?.prenom || '').trim().split(/\s+/)[0];

/** Plein écran après confirmation : aplat bleu qui s'ouvre en cercle depuis la carte, puis « Bienvenue ». */
export function ArrivalOverlay({ user, origin, duration }) {
  const name = firstName(user);
  const x = origin?.x ?? window.innerWidth / 2;
  const y = origin?.y ?? window.innerHeight / 2;
  return createPortal(
    <motion.div
      className="fixed inset-0 z-[10000] flex items-center justify-center text-white"
      style={{ background: BLUE }}
      initial={{ clipPath: `circle(0px at ${x}px ${y}px)` }}
      animate={{ clipPath: `circle(${Math.hypot(window.innerWidth, window.innerHeight)}px at ${x}px ${y}px)` }}
      transition={{ duration: 0.75, ease: [0.76, 0, 0.24, 1] }}
      role="status"
      aria-live="polite"
    >
      <div className="flex flex-col items-center text-center px-6">
        <motion.h2
          className="text-3xl font-bold tracking-tight"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.5 }}
        >
          {name ? `Bienvenue, ${name}` : 'Bienvenue'}
        </motion.h2>
        <motion.p
          className="mt-2 text-white/75"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...spring, delay: 0.62 }}
        >
          Ouverture de vos projets…
        </motion.p>
        <div className="mt-8 w-48 h-[3px] rounded-full bg-white/20 overflow-hidden">
          <motion.div
            className="h-full bg-white rounded-full"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: Math.max(0.3, duration / 1000 - 0.8), delay: 0.7, ease: [0.4, 0, 0.2, 1] }}
          />
        </div>
      </div>
    </motion.div>,
    document.body
  );
}
